import 'twin.macro';

import { Cross } from '@cheqroom/icons';
import { ComponentPropsWithoutRef, forwardRef, ReactNode, useId } from 'react';

import { Icon } from '../Icon/Icon';
import { Labelled, Props as LabelledProps } from '../Labelled/Labelled';
import { Prefix, StyledInput, StyledInputWrapper, Suffix } from './Input.styles';

export interface Props extends Omit<ComponentPropsWithoutRef<'input'>, 'prefix' | 'suffix'>, LabelledProps {
	/**
	 * Determine type of input
	 * @default 'text'
	 */
	type?: 'text' | 'number' | 'email' | 'password' | 'date';
	/** Show clear button */
	clearButton?: boolean;
	/** Callback when value is cleared */
	onClear?: () => void;
	/**
	 * Placeholder text
	 */
	placeholder?: string;
	/** Text to display before value */
	prefix?: ReactNode;
	/** Text to display after value */
	suffix?: ReactNode;
	/** Props to pass to the container */
	containerProps?: Record<string, unknown>;
	propagate?: boolean;
	/** Prevents the input from changing when scrolling */
	preventChangeOnScroll?: boolean;
}

const Input = forwardRef<HTMLInputElement, Props>(
	(
		{
			clearButton = false,
			containerProps,
			error,
			helpText,
			id,
			label,
			labelHidden,
			onChange,
			onClear,
			placeholder,
			prefix,
			readOnly,
			required,
			suffix,
			tabIndex,
			type = 'text',
			value,
			propagate = false,
			preventChangeOnScroll = false,
			...rest
		},
		ref
	) => {
		const generatedId = useId();

		return (
			<Labelled
				error={error}
				helpText={helpText}
				htmlFor={id ?? generatedId}
				label={label}
				labelHidden={labelHidden}
				required={required}
				{...containerProps}
				{...rest}
			>
				<StyledInputWrapper error={error} readOnly={readOnly}>
					{prefix && <Prefix>{prefix}</Prefix>}

					<StyledInput
						{...rest}
						onClick={(event) => {
							event.stopPropagation();
							rest.onClick?.(event);
						}}
						onKeyDown={(event) => {
							!propagate && event.stopPropagation();
							rest.onKeyDown?.(event);
						}}
						onKeyUp={(event) => {
							!propagate && event.stopPropagation();
							rest.onKeyUp?.(event);
						}}
						id={id ?? generatedId}
						onChange={onChange}
						placeholder={placeholder}
						readOnly={readOnly}
						ref={ref}
						required={required}
						tabIndex={tabIndex}
						type={type}
						value={value}
						onWheel={(event) => {
							preventChangeOnScroll && event.currentTarget.blur();
							rest.onWheel?.(event);
						}}
					/>

					{suffix && <Suffix>{suffix}</Suffix>}

					{!!value && !!onClear && clearButton && (
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								onClear();
							}}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.stopPropagation();
									onClear();
								}
							}}
						>
							<Icon source={Cross} size="small" tw="text-gray-500 cursor-pointer focus:outline-none" />
						</button>
					)}
				</StyledInputWrapper>
			</Labelled>
		);
	}
);

Input.displayName = 'Input';
export { Input };
